import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostListener,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {AssetService} from "../../../common/services/asset.service";
import {DomSanitizer, Title} from "@angular/platform-browser";
import {PreLoginSystemConfigurationValues} from "../../../common/configuration/services/prelogin.system.configuration.manager";
import {PatientSearchComponent} from "./patient.search/patient.search.component";
import {PatientDetailsComponent} from "./patient.details/patient.details.component";
import {PatientAccountComponent} from "./patient.account/patient.account.component";
import {SummaryComponent} from "./summary/summary.component";
import {
	ChangePasswordResult, CreateAccountResult,
	IOneTimeCodeDetails,
	IPatientSearchResult
} from "./common/common.types";
import {LoginComponent} from "./login/login.component";
import {OneTimeCodeComponent} from "./one.time.code/one.time.code.component";
import {LogManager} from "../../../common/log/log.manager";
import {LogCat} from "../../../common/log/log.categories";
import {ShowHideApplicationHeaderService} from "../../../common/services/show.hide.application.header.service";
import {TITLE_SUFFIX} from "../../../common/translate/translate.suffix";
import {Observable, zip} from "rxjs";
import {TranslateService} from "../../../common/translate/services/translate.service";
import {takeUntilDestroy} from "take-until-destroy";
import {AppMainComponent} from "../../../app-main/app-main.component";

@Component({
	selector: 'app-login-internal',
	templateUrl: './login-internal.component.html',
	styleUrls: ['./login-internal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginInternalComponent implements OnInit, OnDestroy {
	@ViewChild("Login", {static: false}) login: LoginComponent;
	@ViewChild("PatientSearch", {static: false}) patientSearch: PatientSearchComponent;
	@ViewChild("PatientDetails", {static: false}) patientDetails: PatientDetailsComponent;
	@ViewChild("PatientAccount", {static: false}) patientAccount: PatientAccountComponent;
	@ViewChild("Summary", {static: false}) summary: SummaryComponent;
	@ViewChild("OneTimeCode", {static: false}) oneTimeCode: OneTimeCodeComponent;

	private returnUrl: string;
	public error = '';
	public createAccountActivated = false;
	public forgotPasswordActivated = false;
	public hideBackButton = false;
	public hideCloseButton = false;

	public set LoginDisplayed(value) {
		this.isLoginDisplayed = value;

		if (this.isLoginDisplayed)
		{
			this.setApplicationTitle("Login");
		}
	}

	public set patientSearchDisplayed(value) {
		this.isPatientSearchDisplayed = value;

		if (this.isPatientSearchDisplayed) {
			this.setApplicationTitle("PatientSearch");
		}
	}

	public set patientDetailsDisplayed(value) {
		this.isPatientDetailsDisplayed = value;

		if (this.isPatientDetailsDisplayed) {
			this.setApplicationTitle("PatientDetails");
		}
	}

	public set patientAccountDisplayed(value) {
		this.isPatientAccountDisplayed = value;

		if (this.isPatientAccountDisplayed) {
			this.setApplicationTitle("PatientAccount");
		}
	}

	public set summaryDisplayed(value) {
		this.isSummaryDisplayed = value;

		if (this.isSummaryDisplayed) {
			this.setApplicationTitle("Summary");
		}
	}

	public set oneTimeCodeDisplayed(value) {
		this.isOneTimeCodeDisplayed = value;

		if (this.isOneTimeCodeDisplayed) {
			this.setApplicationTitle("OneTimeCode");
		}
	}

	public isLoginDisplayed = true;
	public isPatientSearchDisplayed = false;
	public isPatientDetailsDisplayed = false;
	public isPatientAccountDisplayed = false;
	public isSummaryDisplayed = false;
	public isOneTimeCodeDisplayed = false;

	private skipPatientDetails = false;
	private allowCreatePatient = false;

	constructor(private route: ActivatedRoute, private router: Router, private assetService: AssetService,
				private changeDetector: ChangeDetectorRef, private sanitizer: DomSanitizer,
				private sysConfig: PreLoginSystemConfigurationValues,
				public showHideApplicationHeaderService: ShowHideApplicationHeaderService, private translateService: TranslateService,
				private titleService: Title) {
		this.sysConfig.GetCreateNewPatientOnAccountCreationEnabledAsync().subscribe(value => {
			this.allowCreatePatient = value;
		});
	}

	ngOnInit() {
		this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
	}

	ngOnDestroy(): void {

	}

	@HostListener("window:beforeunload", ['$event'])
	onBeforeUnload($event: BeforeUnloadEvent) {
		if (this.createAccountActivated || this.forgotPasswordActivated) {
			$event.returnValue = "True";
		}
	}

	public newAccount() {
		this.showHideApplicationHeaderService.hideHeader();
		this.LoginDisplayed = false;
		this.patientSearchDisplayed = true;
		this.createAccountActivated = true;

		this.changeDetector.markForCheck();
	}

	public doForgotPassword() {
		this.showHideApplicationHeaderService.hideHeader();
		this.LoginDisplayed = false;
		this.patientSearchDisplayed = true;
		this.forgotPasswordActivated = true;

		this.changeDetector.markForCheck();
	}

	public searchPatientDone($event: IPatientSearchResult) {
		this.patientSearchDisplayed = false;

		if (this.createAccountActivated) {
			this.searchPatientDoneForAccountCreation($event);
		} else if (this.forgotPasswordActivated) {
			this.searchPatientDoneForForgotPassword($event);
		}

		this.changeDetector.markForCheck();
	}

    public takeFocus($event: boolean) {
        if ($event) {
            setTimeout(() => {
                (this.login).focus();
            }, 0);
        }
    }

	private searchPatientDoneForAccountCreation($event: IPatientSearchResult) {
		if ($event.locatePatientRes.accountExists) {
			this.summaryDisplayed = true;
			this.summary.setPatient($event.patient);
			this.summary.setAccountAlreadyExists();
		} else if (!$event.locatePatientRes.patientExists && !this.allowCreatePatient) {
			this.summaryDisplayed = true;
			this.summary.setPatient($event.patient);
			this.summary.setCreationOfNewPatientNotAllowed();
		} else if ($event.locatePatientRes.multiplePatientsFound) {
			this.summaryDisplayed = true;
			this.summary.setPatient($event.patient);
			this.summary.setMultiplePatientsFoundOnAccountCreation();
		} else if ($event.locatePatientRes.patientExists) {
			this.skipPatientDetails = true;
			this.handleOneTimeCodeGenerationForCreateAccount($event.patient);
		} else {
			this.skipPatientDetails = false;
			this.patientDetailsDisplayed = true;
			this.patientDetails.setPatient($event.patient);
		}
	}

	public continueToOneTimeCodeGeneration(patient: any) {
		this.patientDetailsDisplayed = false;
		this.handleOneTimeCodeGenerationForCreateAccount(patient);
	}

	private handleOneTimeCodeGenerationForCreateAccount(patient: any) {
		this.oneTimeCode.generateOneTimeCode(patient, !this.skipPatientDetails).subscribe(res => {
			if (res.success) {
				this.oneTimeCodeDisplayed = true;
			} else {
				this.summaryDisplayed = true;
				this.summary.setPatient(patient);
				this.summary.setOneTimeCodeGenerationFailedOnAccountCreation(true, false);
			}
			this.changeDetector.markForCheck();
		}, err => {
			LogManager.error(LogCat.Login.ForgotPassword, err);
			this.summaryDisplayed = true;
			this.summary.setPatient(patient);
			this.summary.setOneTimeCodeGenerationFailedOnAccountCreation(false, true);
			this.changeDetector.markForCheck();
		});
	}

	private searchPatientDoneForForgotPassword($event: IPatientSearchResult) {
		if ($event.locatePatientRes.multiplePatientsFound) {
			this.summaryDisplayed = true;
			this.summary.setPatient($event.patient);
			this.summary.setMultiplePatientsFoundOnForgotPassword();
		} else if (!$event.locatePatientRes.accountExists) {
			this.summaryDisplayed = true;
			this.summary.setPatient($event.patient);
			this.summary.setPatientPortalAccountNotFoundOnForgotPassword();
		} else if ($event.locatePatientRes.patientExists) {
			this.skipPatientDetails = true;
			this.handleOneTimeCodeGenerationForForgotPassword($event.patient);
		} else {
			this.summaryDisplayed = true;
			this.summary.setPatient($event.patient);
			this.summary.setPatientNotFoundOnForgotPassword();
		}
	}

	private handleOneTimeCodeGenerationForForgotPassword(patient: any) {
		this.oneTimeCode.generateOneTimeCode(patient, !this.skipPatientDetails).subscribe(res => {
			if (res.success) {
				this.oneTimeCodeDisplayed = true;
			} else {
				this.summaryDisplayed = true;
				this.summary.setPatient(patient);
				this.summary.setOneTimeCodeGenerationFailedOnForgotPassword(true, false);
			}
			this.changeDetector.markForCheck();
		}, err => {
			LogManager.error(LogCat.Login.ForgotPassword, err);
			this.summaryDisplayed = true;
			this.summary.setPatient(patient);
			this.summary.setOneTimeCodeGenerationFailedOnForgotPassword(false, true);
			this.changeDetector.markForCheck();
		});
	}

	public errorDuringPatientSearch(patient: any) {
		this.patientSearchDisplayed = false;
		this.summaryDisplayed = true;
		this.summary.setPatient(patient);
		this.summary.setErrorDuringPatientSearch();
		this.changeDetector.markForCheck();
	}

	public backToSearch(patient: any) {
		this.patientSearchDisplayed = true;
		this.patientDetailsDisplayed = false;

		this.patientSearch.setPatient(patient);
		this.changeDetector.markForCheck();
	}

	public backFromOneTimeCode(patient: any) {
		this.oneTimeCodeDisplayed = false;

		if (this.skipPatientDetails) {
			this.patientSearchDisplayed = true;
			this.patientSearch.setPatient(patient);
		} else {
			this.patientDetailsDisplayed = true;
			this.patientDetails.setPatient(patient);
		}

		this.changeDetector.markForCheck();
	}

	public oneTimeCodeValidationDone($event: IOneTimeCodeDetails) {
		this.oneTimeCodeDisplayed = false;
		this.patientAccountDisplayed = true;
		this.hideBackButton = true;

		if (this.createAccountActivated) {
			this.patientAccount.setPatientForAccountCreation($event.clinicalRequest, !this.skipPatientDetails, $event.oneTimeCode);
		} else {
			this.patientAccount.setPatientForPasswordChanging($event.clinicalRequest, $event.oneTimeCode);
		}
		this.changeDetector.markForCheck();
	}

	public errorDuringOneTimeCodeValidation(patient: any) {
		this.oneTimeCodeDisplayed = false;
		this.summaryDisplayed = true;
		this.summary.setPatient(patient);
		this.summary.setErrorDuringOneTimeCodeValidation();
		this.changeDetector.markForCheck();
	}

	public backFromPatientAccount(patient: any) {
		this.patientAccountDisplayed = false;

		if (this.skipPatientDetails) {
			this.patientSearchDisplayed = true;
			this.patientSearch.setPatient(patient);
		} else {
			this.patientDetailsDisplayed = true;
			this.patientDetails.setPatient(patient);
		}

		this.changeDetector.markForCheck();
	}

	public continueToAccountCreationSummary($event: any) {
		this.patientAccountDisplayed = false;
		this.summaryDisplayed = true;
		if ($event.result === CreateAccountResult.AccountCreatedSuccessfully) {
			this.hideBackButton = true;
			this.hideCloseButton = true;
		}
		this.summary.setPatient($event.patient);
		this.summary.setAccountCreationSummary($event.createAccountResponse.result);
		this.changeDetector.markForCheck();
	}

	public continueToPasswordChangedSummary($event: any) {
		this.patientAccountDisplayed = false;
		this.summaryDisplayed = true;
		if ($event.result === ChangePasswordResult.PasswordChangedSuccessfully) {
			this.hideBackButton = true;
			this.hideCloseButton = true;
		}
		this.summary.setPatient($event.patient);
		this.summary.setPasswordChangedSummary($event.changePasswordResponse.result);
		this.changeDetector.markForCheck();
	}

	public errorDuringAccountCreationOrForgotPassword(patient: any) {
		this.patientAccountDisplayed = false;
		this.summaryDisplayed = true;
		this.summary.setPatient(patient);
		this.summary.setErrorDuringAccountCreationOrForgotPassword();
		this.changeDetector.markForCheck();
	}

	public backToLogin() {
		this.showHideApplicationHeaderService.showHeader();
		this.LoginDisplayed = true;
		this.summaryDisplayed = false;
		this.patientSearchDisplayed = false;
		this.patientDetailsDisplayed = false;
		this.oneTimeCodeDisplayed = false;
		this.patientAccountDisplayed = false;

		this.login.reset();

		this.patientSearch.reset();
		if (this.createAccountActivated) {
			this.patientDetails.reset();
		}
		this.patientAccount.reset();
		this.summary.reset();

		this.createAccountActivated = false;
		this.forgotPasswordActivated = false;
		this.hideBackButton = false;
		this.hideCloseButton = false;
		this.changeDetector.markForCheck();
	}

	public backToSearchFromSummary(patient: any) {
		this.patientSearchDisplayed = true;
		this.summaryDisplayed = false;
		this.hideBackButton = false;
		this.hideCloseButton = false;

		this.patientSearch.setPatient(patient);
		this.patientAccount.reset();
		this.summary.reset();

		this.changeDetector.markForCheck();
	}

	public backToOneTimeCodeFromSummary(patient: any) {
		this.oneTimeCodeDisplayed = true;
		this.summaryDisplayed = false;
		this.hideBackButton = false;
		this.hideCloseButton = false;

		this.handleOneTimeCodeGenerationForCreateAccount(patient);
		this.patientAccount.reset();
		this.summary.reset();

		this.changeDetector.markForCheck();
	}

	public handleBack() {
		if (this.isPatientSearchDisplayed) {
			this.backToLogin();
		} else if (this.isPatientDetailsDisplayed) {
			this.patientDetails.goback();
		} else if (this.isOneTimeCodeDisplayed) {
			this.oneTimeCode.goback();
		} else if (this.isPatientAccountDisplayed) {
			this.patientAccount.goback();
		} else if (this.isSummaryDisplayed) {
			this.summary.goBackToSearch();
		}
	}

	private setApplicationTitle(pageTitleToTranslate?: string) {
		setTimeout(() => {
			const applicationTitle = this.translateService.transform(AppMainComponent.APPLICATION_TITLE_PREFIX, []);
			let titleEntries: Observable<string>[] = [applicationTitle];

			if (this.createAccountActivated) {
				this.addTitleEntry(titleEntries, "CreateAccount");
			} else if (this.forgotPasswordActivated) {
				this.addTitleEntry(titleEntries, "ForgotPassword");
			}

			if (pageTitleToTranslate) {
				this.addTitleEntry(titleEntries, pageTitleToTranslate);
			}

			zip(...titleEntries).pipe(takeUntilDestroy(this)).subscribe((titles: string[]) => {
				const title: string = titles.join(" - ");
				this.titleService.setTitle(title);

				console.log("Set title in login", title);
			});
		}, 0);
	}

	private addTitleEntry(titleEntries: Observable<string>[], newTitleToTranslate: string) {
		const titleToTranslate: string = newTitleToTranslate + TITLE_SUFFIX;
		const title: Observable<string> = this.translateService.transform(titleToTranslate, []);
		titleEntries.push(title);
	}
}
